import { type ReactNode, useRef } from "react";
import Swiper from "@/components/shared/Swiper";
import { SWIPER_SLIDE } from "@/components/shared/swiperSlideClass";
import MethodDetails from "@/components/shopwindow/MethodDetails";
import RecipeSummary from "@/components/shopwindow/RecipeSummary";
import ShopWindowHints from "@/components/shopwindow/ShopWindowHints";
import { eventNames } from "@/modules/Constants";
import appConfig from "@/modules/config";
import { experienceTypes } from "@/modules/ExperienceData";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import { openingSlideType } from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";
import { sanitizeHtml } from "@/modules/sanitizeHtml";

const REGION = "Method Swiper";

type MethodSwiperProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

interface SlideCache {
  slides?: ReactNode[];
  idexperience?: unknown;
  orientation?: unknown;
}

type SlideArticle = Parameters<typeof MethodDetails>[0]["article"];

// The display stays in method-shop-window.css: the vendor .swiper-slide rule would win over a utility
const METHOD_SLIDE = `${SWIPER_SLIDE} tw:[.method-shop-window_.articles_&]:flex-col tw:[.method-shop-window_.articles_&]:justify-center tw:[.method-shop-window_.articles_&]:overflow-auto`;

export default function MethodSwiper(props: MethodSwiperProps) {
  const { config, onApplicationEvent } = props;
  // The slide elements are rebuilt only when the experience or the orientation changes.
  const cache = useRef<SlideCache>({});

  const getSlide = (n: number, article: SlideArticle) => {
    return (
      <div key={`article-slide-${n}`} className={METHOD_SLIDE}>
        <MethodDetails
          experienceState={props.experienceState}
          article={article}
        />
      </div>
    );
  };

  const getRecipeSummarySlide = (n: number) => {
    const experienceState = props.experienceState;
    const experienceData = experienceState.experienceData;
    const applicationData = experienceState.applicationData;

    if (applicationData.isPortrait()) {
      return (
        <div className={METHOD_SLIDE} key={`article-slide-${n}`}>
          <RecipeSummary
            hasDescription={true}
            experienceState={props.experienceState}
          />
        </div>
      );
    } else {
      return (
        <div className={METHOD_SLIDE} key={`article-slide-${n}`}>
          <div className="recipe-summary-body tw:[padding:0_16px]">
            <h2 className="recipe-subtitle header-2">
              {experienceData.subtitle as string}
            </h2>

            <div
              className="recipe-description tw:mt-[10px]"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(experienceData.description ?? ""),
              }}
            />
          </div>
        </div>
      );
    }
  };

  const getHintsSlide = (n: number) => {
    return (
      <div className={METHOD_SLIDE} key={`article-slide-${n}`}>
        <ShopWindowHints />
      </div>
    );
  };

  const getOpeningSlide = (n: number) => {
    const experienceData = props.experienceState.experienceData;

    if (config.openingSlideType() === openingSlideType.CUSTOM) {
      if (experienceData.type === experienceTypes.RECIPE) {
        return getRecipeSummarySlide(n);
      }
    } else if (config.openingSlideType() === openingSlideType.HINTS) {
      return getHintsSlide(n);
    }
  };

  const getSlides = () => {
    const experienceState = props.experienceState;
    const experienceData = experienceState.experienceData;
    const applicationData = experienceState.applicationData;
    const state = cache.current;

    if (
      state.slides === undefined ||
      state.idexperience !== experienceData.idexperience ||
      state.orientation !== applicationData.orientation()
    ) {
      let size = experienceData.getClusterSize(config.clusterName, 0);
      let index = 0;

      state.slides = [];

      const opening = getOpeningSlide(index);
      if (opening) {
        state.slides.push(opening);
        index++;
      }

      const vms = appConfig.getVisibleMethodSteps();
      if (vms !== undefined) {
        size = Math.min(size, vms);
      }

      for (let n = 0; n < size; n++) {
        const article = experienceData.getArticle(config.clusterName, 0, n);
        state.slides.push(getSlide(index++, article as SlideArticle));
      }

      state.idexperience = experienceData.idexperience;
      state.orientation = applicationData.orientation();
    }

    return state.slides;
  };

  return (
    <Swiper
      className="articles tw:grow tw:cursor-pointer tw:[.portrait_.method-shop-window_&]:order-1 tw:[.portrait_.shop-window_&]:mb-[0px]"
      index={config.index}
      hasButtons={true}
      config={{
        effect: "slide",
        // Sizes the slider to the active step so the Up Next chain sits right below it.
        autoHeight: true,
        pagination: {
          el: ".method-shop-window .swiper-pagination",
          clickable: true,
        },
        wrapperClass: "swiper-wrapper",
        slideClass: "swiper-slide",
      }}
      previousActive={index => (index as number) > 0}
      onDecrementItem={() =>
        onApplicationEvent({
          name: eventNames.DECREMENT_SHOP_WINDOW_INDEX,
          region: REGION,
          configName: config.name,
        })
      }
      onIncrementItem={() =>
        onApplicationEvent({
          name: eventNames.INCREMENT_SHOP_WINDOW_INDEX,
          region: REGION,
          configName: config.name,
        })
      }
      onItemChanged={index =>
        onApplicationEvent({
          name: eventNames.SHOP_WINDOW_INDEX_CHANGED,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
      onTouchStart={() =>
        onApplicationEvent({
          name: eventNames.USER_INTERACTION_START,
          region: REGION,
          configName: config.name,
        })
      }
      onTouchEnd={() =>
        onApplicationEvent({
          name: eventNames.USER_INTERACTION_END,
          region: REGION,
          configName: config.name,
        })
      }
      onTransitionStart={index =>
        onApplicationEvent({
          name: eventNames.ARTICLE_TRANSITION_START,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
      onTransitionEnd={index =>
        onApplicationEvent({
          name: eventNames.ARTICLE_TRANSITION_END,
          region: REGION,
          configName: config.name,
          index: index,
        })
      }
    >
      {getSlides()}
    </Swiper>
  );
}
